/**
 * 管理后台「运行与安全 B」页面的接口与类型：视频记录、音频记录、反滥用、会话票据、配置变更。
 *
 * 背景：这五页对应 Alice 的 /video-logs、/audio-logs、/abuse-bans、/session-tickets、/config-audit。
 *   本仓三个服务各管一部分：视频与音频记录、配置变更在 gateway_control（/control/admin/...），
 *   会话票据与反滥用在账号服务（/admin/identity/...），账号与支付两处的审计沿用各自现有的 /audit 接口。
 * 设计意图：
 *   - 不改共用的 admin.ts：这里自己发请求，路径逐字对应后端；遇到 401 先调 adminApi.me() 触发共用 http 层的
 *     会话刷新（同一时刻只刷新一次，避免并发轮换刷新令牌），成功后重试一次；失败时 me() 会通知应用回登录页。
 *   - 配置变更页要把三个服务的审计并成同一种行：控制面由后端整理（/config-audit），账号与支付的行在这里
 *     用与后端相同的规则（auditKind、auditChanges）整理成同样的形状，前端不用关心来源。
 * 关键约束：金额字段是整数 unit（1e-8 美元）；null 表示上游成本未知，不当作 0；错误统一抛 ApiError。
 */
import { adminApi } from './admin.ts'
import { ApiError, type Query } from './http.ts'

// ------------------------------------------------------------------ 公共

export type MediaStatus = 'submitting' | 'queued' | 'running' | 'succeeded' | 'failed' | 'cancelled'
export type ReviewStatus = 'pending' | 'passed' | 'suspect' | 'violation' | 'failed' | 'skipped'
export type ReviewAction = 'pass' | 'block' | 'retry'

export interface MediaCommon {
  request_id: string
  account_id: string
  account_name: string
  end_user: string
  model_id: string
  upstream_model: string
  provider_id: string
  user_cost_units: number | null
  list_cost_units: number | null
  upstream_cost_units: number | null
  billing_mode: string
  created_at: string | null
}

export interface MediaSummaryCommon {
  succeeded: number
  active: number
  failed: number
  user_cost_units: number
  known_user_cost_units: number
  upstream_cost_units: number
  known_cost_tasks: number
  margin_units: number
}

// ------------------------------------------------------------------ 视频

export interface VideoRecord extends MediaCommon {
  task_id: string
  upstream_task_id: string
  resolution: string
  duration: number
  prompt: string
  status: MediaStatus
  error_message: string
  play_url: string
  /** 审核判为违规后，用户侧已看不到播放地址；play_url 仍给运营预览。 */
  blocked: boolean
  review_status: ReviewStatus
  review_scores: Record<string, number> | null
  review_reason: string
  review_error: string
  review_source: string
  reviewed_at: string | null
}

export interface VideoSummary extends MediaSummaryCommon {
  review_violation: number
  review_suspect: number
  review_pending: number
  requested_seconds: number
}

export interface VideoLogs {
  records: VideoRecord[]
  total: number
  limit: number
  offset: number
  summary: VideoSummary
}

export interface VideoQuery {
  user?: string
  model?: string
  prompt?: string
  status?: string
  review_status?: string
  start_date?: string
  end_date?: string
  limit?: number
  offset?: number
}

// ------------------------------------------------------------------ 音频

export type AudioKind = 'speech' | 'transcription'

export interface AudioRecord extends MediaCommon {
  kind: AudioKind
  account_name: string
  status: 'succeeded' | 'failed'
  error_code: string
  characters: number
  duration_ms: number
  text: string
  attempts: number
  duration_total_ms: number
}

export interface AudioSummary extends MediaSummaryCommon {
  speech: number
  transcription: number
  speech_characters: number
  transcription_seconds: number
}

export interface AudioLogs {
  records: AudioRecord[]
  total: number
  limit: number
  offset: number
  summary: AudioSummary
}

export interface AudioQuery {
  kind?: string
  user?: string
  model?: string
  keyword?: string
  status?: string
  start_date?: string
  end_date?: string
  limit?: number
  offset?: number
}

// ------------------------------------------------------------------ 配置变更

export type AuditKind = 'INSERT' | 'UPDATE' | 'DELETE'
export type AuditSource = 'all' | 'control' | 'identity' | 'billing'

export interface AuditChange {
  field: string
  before: string | null
  after: string | null
}

export interface ConfigAuditItem {
  /** 跨来源唯一：来源前缀加各自的 id。 */
  key: string
  id: number
  source: Exclude<AuditSource, 'all'>
  table_name: string
  action_name: string
  entity_key: string
  action: AuditKind
  actor: string
  actor_kind: string
  actor_name: string
  changes: AuditChange[]
  client_addr: string
  created_at: string | null
}

export interface ConfigAuditPage {
  total: number
  page: number
  page_size: number
  items: ConfigAuditItem[]
  summary: { pricing: number; automatic: number; actors: number }
}

export interface ConfigAuditFilters {
  tables: { value: string; label: string; count: number }[]
  actors: { value: string; source: string; name: string; count: number }[]
}

export interface ConfigAuditQuery {
  table?: string
  actor?: string
  entity?: string
  action?: string
  days?: number
  page?: number
  page_size?: number
}

// ------------------------------------------------------------------ 会话票据与反滥用

export type SessionState = 'active' | 'revoked' | 'expired' | 'reuse_detected' | ''

export interface TicketUser {
  account_id: string
  email: string
  name: string
  internal: boolean
  status: SessionState
  sessions: number
  active: number
  revoked: number
  expired: number
  admin_sessions: number
  ips: number
  reuse_events: number
  last_ip: string
  user_agent: string
  last_active: string
}

export interface TicketHandshake {
  id: number
  time: string
  account_id: string
  email: string
  name: string
  result: 'success' | 'revoked' | 'reuse_detected'
  reason: string
  ip: string
  kind: 'console' | 'admin'
  user_agent: string
}

export interface TicketSnapshot {
  window_days: number
  policy: { console_ttl_hours: number; admin_ttl_hours: number; console_access_ttl_s: number; admin_access_ttl_s: number }
  total: number
  active: number
  revoked: number
  expired: number
  rotated: number
  console: number
  admin: number
  accounts: number
  reuse_events: number
  reuse_accounts: number
  users: TicketUser[]
  handshakes: TicketHandshake[]
}

export interface AbuseBan {
  id: number
  account_id: string
  email: string
  name: string
  internal: boolean
  ban_type: string
  reason: string
  banned_by: string
  banned_at: string
  lifted_at: string | null
  lifted_by: string | null
  lift_reason: string
  status: 'active' | 'lifted'
}

export interface AbuseBans {
  total: number
  active: number
  users: number
  bans: AbuseBan[]
}

// ------------------------------------------------------------------ 请求层

function failure(status: number, body: unknown): ApiError {
  const err = (body as { error?: { code?: string; message?: string } } | null)?.error
  return new ApiError(status, err?.code ?? `http_${status}`, err?.message ?? 'Request failed')
}

function url(path: string, query?: Query): string {
  if (!query) return path
  const qs = new URLSearchParams()
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === '' || v === 'all') continue
    qs.set(k, String(v))
  }
  const s = qs.toString()
  return s ? `${path}?${s}` : path
}

async function request<T>(method: string, path: string, query?: Query, body?: unknown): Promise<T> {
  const target = url(path, query)
  for (let attempt = 0; attempt < 2; attempt++) {
    let resp: Response
    try {
      resp = await fetch(target, {
        method,
        credentials: 'same-origin',
        headers: body === undefined ? { Accept: 'application/json' } : { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: body === undefined ? undefined : JSON.stringify(body),
      })
    } catch (e) {
      console.error('[api] 网络错误', method, target, e)
      throw new ApiError(0, 'network_error', 'Network error')
    }
    const text = await resp.text()
    let parsed: unknown
    try {
      parsed = text ? JSON.parse(text) : undefined
    } catch {
      parsed = undefined
    }
    if (resp.status === 401 && attempt === 0) {
      try {
        await adminApi.me()
      } catch (e) {
        console.warn('[api] 会话刷新失败，不再重试', e)
        throw failure(resp.status, parsed)
      }
      continue
    }
    if (!resp.ok) {
      const err = failure(resp.status, parsed)
      console.warn('[api] 请求失败', method, target, err.status, err.code)
      throw err
    }
    return parsed as T
  }
  throw new ApiError(401, 'not_logged_in', 'Login required.')
}

const C = '/control/admin'

// ------------------------------------------------------------------ 配置变更：三个来源并成同一种行

const INSERT_SUFFIXES = new Set(['create', 'created', 'signup', 'member_added', 'credit', 'bonus'])
const DELETE_SUFFIXES = new Set(['delete', 'deleted', 'revoke', 'revoked', 'member_removed', 'purged', 'clawback'])
export const PRICING_PREFIXES = new Set(['model', 'channel_model', 'channel', 'product'])
const MAX_VALUE_CHARS = 200

/** 动作后缀归成新建、修改、删除三类，与 runtime2_audit.kind_of 同一规则。 */
export function auditKind(action: string): AuditKind {
  const suffix = action.includes('.') ? action.slice(action.indexOf('.') + 1) : ''
  if (INSERT_SUFFIXES.has(suffix)) return 'INSERT'
  return DELETE_SUFFIXES.has(suffix) ? 'DELETE' : 'UPDATE'
}

function render(value: unknown): string {
  if (value === null || value === undefined) return '∅'
  if (typeof value === 'boolean') return value ? 'true' : 'false'
  const text = typeof value === 'object' ? JSON.stringify(value) : String(value)
  return text.length > MAX_VALUE_CHARS ? `${text.slice(0, MAX_VALUE_CHARS)}…` : text
}

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v)
const isEmpty = (v: unknown) => v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0) || (isRecord(v) && Object.keys(v).length === 0)

/** detail → 「字段 + 改前 + 改后」，与 runtime2_audit.build_changes 同一规则。 */
export function auditChanges(detail: Record<string, unknown>, kind: AuditKind): AuditChange[] {
  const { before, after } = detail
  if (isRecord(before) && isRecord(after)) {
    const fields = [...new Set([...Object.keys(before), ...Object.keys(after)])].filter((k) => JSON.stringify(before[k]) !== JSON.stringify(after[k]))
    const rest = Object.entries(detail).filter(([k]) => k !== 'before' && k !== 'after')
    return [
      ...fields.map((k) => ({ field: k, before: render(before[k]), after: render(after[k]) })),
      ...rest.map(([k, v]) => ({ field: k, before: null, after: render(v) })),
    ]
  }
  return Object.entries(detail)
    .filter(([, v]) => !isEmpty(v))
    .map(([k, v]) => ({ field: k, before: kind === 'DELETE' ? render(v) : null, after: kind === 'DELETE' ? null : render(v) }))
}

interface RawAudit {
  id: number
  actor_kind: string
  actor_id: string
  action: string
  target: string
  detail: Record<string, unknown>
  ip: string
  created_at: string
}

function fromRaw(source: 'identity' | 'billing', r: RawAudit): ConfigAuditItem {
  const action = auditKind(r.action)
  return {
    key: `${source}:${r.id}`,
    id: r.id,
    source,
    table_name: r.action.split('.')[0] ?? '',
    action_name: r.action,
    entity_key: r.target,
    action,
    actor: r.actor_id,
    actor_kind: r.actor_kind,
    actor_name: '',
    changes: auditChanges(r.detail ?? {}, action),
    client_addr: r.ip,
    created_at: r.created_at,
  }
}

const MERGE_LIMIT = 200

export const runtime2Api = {
  // 视频与音频记录
  videoLogs: (q: VideoQuery = {}) => request<VideoLogs>('GET', `${C}/video-logs`, q as Query),
  reviewVideo: (requestId: string, action: ReviewAction) =>
    request<{ ok: boolean; request_id: string; review_status: ReviewStatus; enforced: boolean }>(
      'POST',
      `${C}/video-logs/${encodeURIComponent(requestId)}/review`,
      undefined,
      { action },
    ),
  audioLogs: (q: AudioQuery = {}) => request<AudioLogs>('GET', `${C}/audio-logs`, q as Query),

  // 账号服务：会话票据与反滥用
  sessionTickets: (days = 7) => request<TicketSnapshot>('GET', '/admin/identity/session-tickets', { days }),
  abuseBans: (limit = 100) => request<AbuseBans>('GET', '/admin/identity/abuse-bans', { limit }),

  // 配置变更：控制面由后端整理并分页
  controlAudit: async (q: ConfigAuditQuery = {}): Promise<ConfigAuditPage> => {
    const r = await request<Omit<ConfigAuditPage, 'items'> & { items: Omit<ConfigAuditItem, 'key' | 'source'>[] }>('GET', `${C}/config-audit`, q as Query)
    return { ...r, items: r.items.map((i) => ({ ...i, source: 'control' as const, key: `control:${i.id}` })) }
  },
  auditFilters: (days = 90) => request<ConfigAuditFilters>('GET', `${C}/config-audit/filters`, { days }),

  /** 账号服务最近的审计（接口自己的上限 500，这里取 MERGE_LIMIT 条）。 */
  identityAuditRows: async (): Promise<ConfigAuditItem[]> => {
    const r = await request<{ data: RawAudit[] }>('GET', '/admin/identity/audit', { limit: MERGE_LIMIT })
    return r.data.map((x) => fromRaw('identity', x))
  },
  /** 支付服务最近的审计（接口上限 200）。 */
  billingAuditRows: async (): Promise<ConfigAuditItem[]> => {
    const r = await request<{ data: RawAudit[] }>('GET', '/billing/admin/audit', { limit: MERGE_LIMIT })
    return r.data.map((x) => fromRaw('billing', x))
  },
}
